import { useEffect, useState } from 'react';

import { usePrefersReducedMotion } from '@chakra-ui/react';

const TYPEWRITER_TYPE_MS = 50;
const TYPEWRITER_DELETE_MS = 30;
const TYPEWRITER_HOLD_MS = 2500;
const TYPEWRITER_GAP_MS = 400;

export type UseTypewriterPlaceholderOptions = {
  introText?: string;
  isPaused: boolean;
  terms: readonly string[];
};

export type UseTypewriterPlaceholderResult = {
  prefersReducedMotion: boolean;
  text: string;
};

export function useTypewriterPlaceholder({
  introText,
  isPaused,
  terms,
}: UseTypewriterPlaceholderOptions): UseTypewriterPlaceholderResult {
  const prefersReducedMotion = Boolean(usePrefersReducedMotion());
  const fallbackText = introText || terms[0] || '';
  const [text, setText] = useState(fallbackText);

  useEffect(() => {
    const cycle = introText ? [introText, ...terms] : terms;
    const loopStartIndex = introText ? 1 : 0;

    if (prefersReducedMotion || isPaused || cycle.length === 0) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let termIndex = 0;
    let charIndex = introText ? introText.length : 0;

    const schedule = (delayMs: number, next: () => void) => {
      timeoutId = setTimeout(() => {
        if (cancelled) {
          return;
        }

        next();
      }, delayMs);
    };

    const typeNext = () => {
      const term = cycle[termIndex] ?? '';
      charIndex += 1;
      setText(term.slice(0, charIndex));

      if (charIndex >= term.length) {
        schedule(TYPEWRITER_HOLD_MS, holdComplete);
        return;
      }

      schedule(TYPEWRITER_TYPE_MS, typeNext);
    };

    const holdComplete = () => {
      schedule(TYPEWRITER_DELETE_MS, deleteNext);
    };

    const deleteNext = () => {
      const term = cycle[termIndex] ?? '';
      charIndex -= 1;
      setText(term.slice(0, Math.max(charIndex, 0)));

      if (charIndex <= 0) {
        schedule(TYPEWRITER_GAP_MS, gapComplete);
        return;
      }

      schedule(TYPEWRITER_DELETE_MS, deleteNext);
    };

    const gapComplete = () => {
      termIndex += 1;
      if (termIndex >= cycle.length) {
        termIndex = loopStartIndex % cycle.length;
      }
      charIndex = 0;
      schedule(TYPEWRITER_TYPE_MS, typeNext);
    };

    if (introText) {
      setText(introText);
      schedule(TYPEWRITER_HOLD_MS, holdComplete);
    } else {
      setText('');
      schedule(TYPEWRITER_TYPE_MS, typeNext);
    }

    return () => {
      cancelled = true;

      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [introText, isPaused, prefersReducedMotion, terms]);

  return {
    prefersReducedMotion,
    text: prefersReducedMotion ? fallbackText : text,
  };
}
